class ProductPreviewDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentProduct = null;
    this.selectedOptions = {};
    this.overlay = this.querySelector('.product-preview-drawer__overlay');
    this.drawer = this.querySelector('.product-preview-drawer__inner');
    this.loadingElement = this.querySelector('.product-preview-drawer__loading');
    this.mainElement = this.querySelector('.product-preview-drawer__main');
    this.variantChangeUnsubscriber = null;
    
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.close();
      });
    }
  }
  
  connectedCallback() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => {
          if (this.isOpen && event.data && event.data.variant) {
            this.syncWithPDPVariant(event.data.variant);
          }
        }
      );
    }
  }
  
  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }
  }
  
  syncWithPDPVariant(variant) {
    if (!this.currentProduct?.variantData || !variant) return;
    
    const { variants } = this.currentProduct.variantData;
    const matchingVariant = variants.find(v => v.id === variant.id);
    if (!matchingVariant) return;
    
    const { options: optionData } = this.currentProduct.variantData;
    
    let option1, option2, option3;
    if (variant.option1 !== undefined) {
      option1 = variant.option1;
      option2 = variant.option2;
      option3 = variant.option3;
    } else if (variant.options && Array.isArray(variant.options)) {
      option1 = variant.options[0];
      option2 = variant.options[1];
      option3 = variant.options[2];
    } else {
      option1 = matchingVariant.option1;
      option2 = matchingVariant.option2;
      option3 = matchingVariant.option3;
    }
    
    if (optionData.fit) {
      const pos = optionData.fit.position;
      const fitValue = pos === 1 ? option1 : pos === 2 ? option2 : option3;
      if (fitValue) {
        this.selectedOptions.fit = fitValue;
        const fitInput = this.mainElement.querySelector(`input[name="fit-option"][value="${CSS.escape(fitValue)}"]`);
        if (fitInput && !fitInput.disabled) {
          fitInput.checked = true;
          const label = fitInput.closest('label');
          if (label) {
            this.mainElement.querySelectorAll('input[name="fit-option"]').forEach(inp => {
              const lbl = inp.closest('label');
              if (lbl) lbl.classList.remove('product-preview-drawer__fit-option--selected');
            });
            label.classList.add('product-preview-drawer__fit-option--selected');
          }
        }
      }
    }
    
    if (optionData.color) {
      const pos = optionData.color.position;
      const colorValue = pos === 1 ? option1 : pos === 2 ? option2 : option3;
      if (colorValue) {
        this.selectedOptions.color = colorValue;
        const colorInput = this.mainElement.querySelector(`input[name="color-option"][value="${CSS.escape(colorValue)}"]`);
        if (colorInput && !colorInput.disabled) {
          colorInput.checked = true;
          const label = colorInput.closest('label');
          if (label) {
            this.mainElement.querySelectorAll('input[name="color-option"]').forEach(inp => {
              const lbl = inp.closest('label');
              if (lbl) lbl.classList.remove('product-preview-drawer__color-option--selected');
            });
            label.classList.add('product-preview-drawer__color-option--selected');
          }
        }
      }
    }
    
    if (optionData.size) {
      const pos = optionData.size.position;
      const sizeValue = pos === 1 ? option1 : pos === 2 ? option2 : option3;
      if (sizeValue) {
        this.selectedOptions.size = sizeValue;
        const sizeInput = this.mainElement.querySelector(`input[name="size-option"][value="${CSS.escape(sizeValue)}"]`);
        if (sizeInput && !sizeInput.disabled) {
          sizeInput.checked = true;
          const label = sizeInput.closest('label');
          if (label) {
            this.mainElement.querySelectorAll('input[name="size-option"]').forEach(inp => {
              const lbl = inp.closest('label');
              if (lbl) lbl.classList.remove('product-preview-drawer__size-option--selected');
            });
            label.classList.add('product-preview-drawer__size-option--selected');
          }
        }
      }
    }
    
    this.updateSelectedVariant();
  }
  
  async open(productUrl) {
    if (!productUrl) {
      console.error('Product URL is required');
      return;
    }
    
    this.isOpen = true;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    
    if (this.loadingElement) {
      this.loadingElement.style.display = 'block';
    }
    if (this.mainElement) {
      this.mainElement.style.display = 'none';
    }
    
    try {
      let fetchPrefix = '';
      if (window.Shopify?.routes?.root?.includes(`/${window.Shopify.locale}/`)) {
        fetchPrefix = `/${window.Shopify.locale}`;
      }

      if (/^https?:\/\//i.test(productUrl)) {
        try {
          productUrl = new URL(productUrl).pathname;
        } catch (_) {}
      }

      if (!productUrl.startsWith('/')) {
        productUrl = '/' + productUrl;
      }

      if (fetchPrefix && productUrl.startsWith(`${fetchPrefix}/`)) {
        fetchPrefix = '';
      }

      const response = await fetch(`${fetchPrefix}${productUrl}?view=preview`);
      if (!response.ok) {
        throw new Error(`Failed to fetch product preview: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const productData = this.extractProductData(doc);
      this.currentProduct = productData;
      
      this.renderProductPreview(productData);
      
      if (this.loadingElement) {
        this.loadingElement.style.display = 'none';
      }
      if (this.mainElement) {
        this.mainElement.style.display = 'block';
      }
      
      const firstFocusable = this.drawer?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    } catch (error) {
      console.error('Error loading product preview:', error);
      if (this.loadingElement) {
        this.loadingElement.style.display = 'none';
      }
      if (this.mainElement) {
        this.mainElement.innerHTML = '<p>Error loading product preview. Please try again.</p>';
        this.mainElement.style.display = 'block';
      }
    }
  }
  
  extractProductData(doc) {
    const previewData = doc.querySelector('.product-preview-data');
    if (!previewData) {
      throw new Error('Product preview data not found');
    }
    
    const title = previewData.querySelector('.product-preview-data__title')?.textContent?.trim() || '';
    const priceHtml = previewData.querySelector('.product-preview-data__price')?.innerHTML || '';
    const image = previewData.querySelector('.product-preview-data__image img')?.src || '';
    const url = previewData.querySelector('.product-preview-data__url')?.textContent?.trim() || '';
    
    const variantsScript = previewData.querySelector('.product-preview-data__variants');
    let variantData = null;
    if (variantsScript) {
      try {
        variantData = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Error parsing variant data:', e);
      }
    }
    
    const performanceDescription = previewData.querySelector('.product-preview-data__performance-description')?.innerHTML || '';
    const performanceFeatures = Array.from(previewData.querySelectorAll('.product-preview-data__performance-feature')).map(feature => ({
      icon: feature.querySelector('img')?.src || '',
      text: feature.querySelector('span')?.textContent?.trim() || ''
    }));

    let siblings = null;
    const siblingsScript = previewData.querySelector('.product-preview-data__siblings');
    if (siblingsScript) {
      try {
        siblings = JSON.parse(siblingsScript.textContent);
      } catch (e) {
        console.error('Error parsing siblings data:', e);
      }
    }

    let fitSiblings = null;
    const fitSiblingsScript = previewData.querySelector('.product-preview-data__fit-siblings');
    if (fitSiblingsScript) {
      try {
        fitSiblings = JSON.parse(fitSiblingsScript.textContent);
      } catch (e) {
        console.error('Error parsing fit siblings data:', e);
      }
    }

    return {
      title,
      priceHtml,
      image,
      url,
      variantData,
      performanceDescription,
      performanceFeatures,
      siblings,
      fitSiblings
    };
  }
  
  findVariant(options) {
    if (!this.currentProduct?.variantData) return null;
    
    const { variants, options: optionData } = this.currentProduct.variantData;
    
    return variants.find(variant => {
      let matches = true;
      
      if (optionData.fit && options.fit) {
        const pos = optionData.fit.position;
        if (pos === 1 && variant.option1 !== options.fit) matches = false;
        if (pos === 2 && variant.option2 !== options.fit) matches = false;
        if (pos === 3 && variant.option3 !== options.fit) matches = false;
      }
      
      if (optionData.color && options.color) {
        const pos = optionData.color.position;
        if (pos === 1 && variant.option1 !== options.color) matches = false;
        if (pos === 2 && variant.option2 !== options.color) matches = false;
        if (pos === 3 && variant.option3 !== options.color) matches = false;
      }
      
      if (optionData.size && options.size) {
        const pos = optionData.size.position;
        if (pos === 1 && variant.option1 !== options.size) matches = false;
        if (pos === 2 && variant.option2 !== options.size) matches = false;
        if (pos === 3 && variant.option3 !== options.size) matches = false;
      }
      
      return matches;
    });
  }
  
  updateSelectedVariant() {
    if (!this.currentProduct?.variantData) return;
    
    const { variants, options: optionData } = this.currentProduct.variantData;
    
    const variant = this.findVariant(this.selectedOptions);
    
    let selectedVariant = variant;
    if (!selectedVariant) {
      selectedVariant = variants.find(v => {
        let matches = true;
        if (optionData.fit && this.selectedOptions.fit) {
          const pos = optionData.fit.position;
          if (pos === 1 && v.option1 !== this.selectedOptions.fit) matches = false;
          if (pos === 2 && v.option2 !== this.selectedOptions.fit) matches = false;
          if (pos === 3 && v.option3 !== this.selectedOptions.fit) matches = false;
        }
        if (optionData.color && this.selectedOptions.color) {
          const pos = optionData.color.position;
          if (pos === 1 && v.option1 !== this.selectedOptions.color) matches = false;
          if (pos === 2 && v.option2 !== this.selectedOptions.color) matches = false;
          if (pos === 3 && v.option3 !== this.selectedOptions.color) matches = false;
        }
        if (optionData.size && this.selectedOptions.size) {
          const pos = optionData.size.position;
          if (pos === 1 && v.option1 !== this.selectedOptions.size) matches = false;
          if (pos === 2 && v.option2 !== this.selectedOptions.size) matches = false;
          if (pos === 3 && v.option3 !== this.selectedOptions.size) matches = false;
        }
        return matches;
      });
    }
    
    this.updateSizeAvailability();
    
    if (!selectedVariant) return;
    
    const variantInput = this.mainElement.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = selectedVariant.id;
    }
    
    const priceElement = this.mainElement.querySelector('.product-preview-drawer__price');
    if (priceElement) {
      const price = (selectedVariant.price / 100).toFixed(2).replace('.', ',');
      let priceHtml = '';
      
      if (selectedVariant.compareAtPrice && selectedVariant.compareAtPrice > selectedVariant.price) {
        const comparePrice = (selectedVariant.compareAtPrice / 100).toFixed(2).replace('.', ',');
        const discount = Math.round(((selectedVariant.compareAtPrice - selectedVariant.price) / selectedVariant.compareAtPrice) * 100);
        priceHtml = `
          <div class="price__sale">
            <div class="price__sale-inner">
              <s>${comparePrice} €</s>
              <span class="price__discount-badge">-${discount}%</span>
            </div>
            <div class="price__sale-inner">
              <ins>${price} €</ins>
            </div>
            <div class="price__history">
              <span class="price__history-text">Prezzo più basso ultimi 30gg: ${comparePrice} €</span>
            </div>
          </div>
        `;
      } else {
        priceHtml = `<div class="price__regular"><span class="price-max">${price} €</span></div>`;
      }
      
      priceElement.innerHTML = priceHtml;
    }
    
    const addToCartBtn = this.mainElement.querySelector('.product-preview-drawer__add-to-cart-btn');
    if (addToCartBtn) {
      addToCartBtn.disabled = !selectedVariant.available;
    }
  }
  
  updateSizeAvailability() {
    if (!this.currentProduct?.variantData) return;
    
    const { variants, options: optionData } = this.currentProduct.variantData;
    
    if (!optionData.size) return;
    
    const sizeInputs = this.mainElement.querySelectorAll('input[name="size-option"]');
    let currentSelectedSize = null;
    let hasAvailableSize = false;
    
    sizeInputs.forEach(input => {
      const sizeValue = input.value;
      const sizePos = optionData.size.position;
      
      let isAvailable = false;
      for (const variant of variants) {
        let matches = true;
        
        if (optionData.fit && this.selectedOptions.fit) {
          const fitPos = optionData.fit.position;
          if (fitPos === 1 && variant.option1 !== this.selectedOptions.fit) matches = false;
          if (fitPos === 2 && variant.option2 !== this.selectedOptions.fit) matches = false;
          if (fitPos === 3 && variant.option3 !== this.selectedOptions.fit) matches = false;
        }
        
        if (optionData.color && this.selectedOptions.color) {
          const colorPos = optionData.color.position;
          if (colorPos === 1 && variant.option1 !== this.selectedOptions.color) matches = false;
          if (colorPos === 2 && variant.option2 !== this.selectedOptions.color) matches = false;
          if (colorPos === 3 && variant.option3 !== this.selectedOptions.color) matches = false;
        }
        
        if (sizePos === 1 && variant.option1 === sizeValue && variant.available) {
          if (matches) {
            isAvailable = true;
            break;
          }
        } else if (sizePos === 2 && variant.option2 === sizeValue && variant.available) {
          if (matches) {
            isAvailable = true;
            break;
          }
        } else if (sizePos === 3 && variant.option3 === sizeValue && variant.available) {
          if (matches) {
            isAvailable = true;
            break;
          }
        }
      }
      
      if (input.checked) {
        currentSelectedSize = input;
      }
      
      input.disabled = !isAvailable;
      const label = input.closest('label');
      if (label) {
        label.classList.toggle('product-preview-drawer__size-option--disabled', !isAvailable);
        if (!isAvailable && label.classList.contains('product-preview-drawer__size-option--selected')) {
          label.classList.remove('product-preview-drawer__size-option--selected');
          input.checked = false;
          this.selectedOptions.size = null;
        }
        if (isAvailable) {
          hasAvailableSize = true;
        }
      }
    });
    
    if (currentSelectedSize && currentSelectedSize.disabled && hasAvailableSize) {
      const firstAvailable = Array.from(sizeInputs).find(inp => !inp.disabled);
      if (firstAvailable) {
        firstAvailable.checked = true;
        this.selectedOptions.size = firstAvailable.value;
        const label = firstAvailable.closest('label');
        if (label) {
          label.classList.add('product-preview-drawer__size-option--selected');
        }
      }
    }
  }
  
  renderProductPreview(data) {
    if (!data.variantData) {
      this.mainElement.innerHTML = '<p>Error: Product variant data not available</p>';
      return;
    }
    
    const { options: optionData, selectedVariantId } = data.variantData;
    
    const defaultVariant = data.variantData.variants.find(v => v.id === selectedVariantId);
    if (defaultVariant) {
      if (optionData.fit) {
        const pos = optionData.fit.position;
        this.selectedOptions.fit = pos === 1 ? defaultVariant.option1 : pos === 2 ? defaultVariant.option2 : defaultVariant.option3;
      }
      if (optionData.color) {
        const pos = optionData.color.position;
        this.selectedOptions.color = pos === 1 ? defaultVariant.option1 : pos === 2 ? defaultVariant.option2 : defaultVariant.option3;
      }
      if (optionData.size) {
        const pos = optionData.size.position;
        this.selectedOptions.size = pos === 1 ? defaultVariant.option1 : pos === 2 ? defaultVariant.option2 : defaultVariant.option3;
      }
    }
    
    let fitOptionsHtml = '';
    if (data.fitSiblings && data.fitSiblings.length > 1) {
      const fitLabel = optionData.fit ? optionData.fit.name : 'Fit';
      const fitSiblingButtons = data.fitSiblings.map(sib => {
        const isActive = sib.isCurrent;
        return `<button type="button" class="product-preview-drawer__fit-option product-preview-drawer__fit-sibling-link${isActive ? ' product-preview-drawer__fit-option--selected' : ''}" data-product-url="${sib.url}" title="${sib.label}">
          <span>${sib.label.toLowerCase()}</span>
        </button>`;
      }).join('');

      fitOptionsHtml = `
        <div class="product-preview-drawer__variant-option">
          <div class="product-preview-drawer__variant-label">${fitLabel.toUpperCase()}</div>
          <div class="product-preview-drawer__fit-options">${fitSiblingButtons}</div>
        </div>
      `;
    } else if (optionData.fit) {
      const fitValues = optionData.fit.values.map((value, index) => {
        const isSelected = value.selected;
        const separator = index < optionData.fit.values.length - 1 ? '  ' : '';
        return `<label class="product-preview-drawer__fit-option${isSelected ? ' product-preview-drawer__fit-option--selected' : ''}${!value.available ? ' product-preview-drawer__fit-option--disabled' : ''}">
          <input type="radio" name="fit-option" value="${value.value}" ${isSelected ? 'checked' : ''} ${!value.available ? 'disabled' : ''} style="display: none;">
          <span>${value.value.toLowerCase()}</span>
        </label>${separator}`;
      }).join('');

      fitOptionsHtml = `
        <div class="product-preview-drawer__variant-option">
          <div class="product-preview-drawer__variant-label">${optionData.fit.name.toUpperCase()}</div>
          <div class="product-preview-drawer__fit-options">${fitValues}</div>
        </div>
      `;
    }
    
    let colorOptionsHtml = '';
    if (data.siblings && data.siblings.length > 1) {
      const colorLabel = optionData.color ? optionData.color.name : 'Color';
      const siblingSwatches = data.siblings.map(sibling => {
        const isActive = sibling.isCurrent;
        const rawSwatch = sibling.swatch || '';
        const swatch = typeof rawSwatch === 'string' ? rawSwatch : '';
        const isImage = swatch && (swatch.includes('http') || swatch.startsWith('/'));
        const swatchStyle = swatch ? (isImage ? `background-image: url(${swatch}); background-size: cover;` : `background-color: ${swatch};`) : '';
        return `<a href="${sibling.url}" class="product-preview-drawer__color-option${isActive ? ' product-preview-drawer__color-option--selected' : ''}" title="${sibling.colorLabel}">
          <span class="product-preview-drawer__color-swatch" style="${swatchStyle}"></span>
        </a>`;
      }).join('');

      colorOptionsHtml = `
        <div class="product-preview-drawer__variant-option">
          <div class="product-preview-drawer__variant-label">${colorLabel.toUpperCase()}</div>
          <div class="product-preview-drawer__color-options">${siblingSwatches}</div>
        </div>
      `;
    } else if (optionData.color) {
      const colorValues = optionData.color.values.map(value => {
        const isSelected = value.selected;
        const valueSwatch = typeof value.swatch === 'string' ? value.swatch : '';
        const isSwatchImage = valueSwatch && (valueSwatch.includes('http') || valueSwatch.startsWith('/'));
        const swatchStyle = valueSwatch ? (isSwatchImage ? `background-image: url(${valueSwatch}); background-size: cover;` : `background-color: ${valueSwatch};`) : '';
        return `<label class="product-preview-drawer__color-option${isSelected ? ' product-preview-drawer__color-option--selected' : ''}${!value.available ? ' product-preview-drawer__color-option--disabled' : ''}">
          <input type="radio" name="color-option" value="${value.value}" ${isSelected ? 'checked' : ''} ${!value.available ? 'disabled' : ''} style="display: none;">
          <span class="product-preview-drawer__color-swatch" style="${swatchStyle}"></span>
        </label>`;
      }).join('');

      colorOptionsHtml = `
        <div class="product-preview-drawer__variant-option">
          <div class="product-preview-drawer__variant-label">${optionData.color.name.toUpperCase()}</div>
          <div class="product-preview-drawer__color-options">${colorValues}</div>
        </div>
      `;
    }
    
    let sizeOptionsHtml = '';
    if (optionData.size) {
      const sizeValues = optionData.size.values.map(value => {
        const isSelected = value.selected;
        return `<label class="product-preview-drawer__size-option${isSelected ? ' product-preview-drawer__size-option--selected' : ''}${!value.available ? ' product-preview-drawer__size-option--disabled' : ''}">
          <input type="radio" name="size-option" value="${value.value}" ${isSelected ? 'checked' : ''} ${!value.available ? 'disabled' : ''} style="display: none;">
          <span>${value.value}</span>
        </label>`;
      }).join('');
      
      sizeOptionsHtml = `
        <div class="product-preview-drawer__variant-option">
          <div class="product-preview-drawer__variant-label">${optionData.size.name.toUpperCase()}</div>
          <div class="product-preview-drawer__size-options">${sizeValues}</div>
        </div>
      `;
    }
    
    let performanceHtml = '';
    if (data.performanceFeatures && data.performanceFeatures.length > 0) {
      const featuresHtml = data.performanceFeatures.map(feature => `
        <div class="product-preview-drawer__performance-feature">
          ${feature.icon ? `<img src="${feature.icon}" alt="${feature.text}" width="50" height="50">` : ''}
          <span>${feature.text}</span>
        </div>
      `).join('');
      
      performanceHtml = `
        <div class="product-preview-drawer__performance">
            <div class="product-preview-drawer__performance-features">${featuresHtml}</div>
        </div>
      `;
    }
    
    const header = this.querySelector('.product-preview-drawer__head');
    if (header) {
      const existingTitle = header.querySelector('.product-preview-drawer__title');
      if (existingTitle) existingTitle.remove();
      const titleElement = document.createElement('h2');
      titleElement.className = 'product-preview-drawer__title';
      titleElement.textContent = data.title;
      header.insertBefore(titleElement, header.firstChild);
    }
    
    const html = `
      <div class="product-preview-drawer__product">
        <div class="product-preview-drawer__price">
          ${data.priceHtml}
        </div>
        
        ${fitOptionsHtml}
        ${colorOptionsHtml}
        ${sizeOptionsHtml}
        
        <div class="product-preview-drawer__actions">
          <form class="product-preview-drawer__add-to-cart-form">
            <input type="hidden" name="id" value="${selectedVariantId}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="product-preview-drawer__add-to-cart-btn">
              AGGIUNGI AL CARRELLO
            </button>
          </form>
          
          <a href="${data.url}" class="product-preview-drawer__view-product-btn">
            VAI ALLA PAGINA PRODOTTO
          </a>
        </div>
        
        ${performanceHtml}
      </div>
    `;
    
    this.mainElement.innerHTML = html;
    
    this.setupVariantListeners();
    this.setupSiblingColorListeners();
    this.setupSiblingFitListeners();

    const form = this.mainElement.querySelector('.product-preview-drawer__add-to-cart-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart(form);
      });
    }
    
    this.updateSelectedVariant();
  }
  
  setupSiblingColorListeners() {
    const siblingLinks = this.mainElement.querySelectorAll('.product-preview-drawer__color-options a.product-preview-drawer__color-option');
    siblingLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const productUrl = link.getAttribute('href');
        if (productUrl) {
          this.mainElement.innerHTML = '';
          const header = this.querySelector('.product-preview-drawer__head');
          if (header) {
            const titleEl = header.querySelector('.product-preview-drawer__title');
            if (titleEl) titleEl.remove();
          }
          this.open(productUrl);
        }
      });
    });
  }

  setupSiblingFitListeners() {
    const fitSiblingBtns = this.mainElement.querySelectorAll('.product-preview-drawer__fit-sibling-link');
    fitSiblingBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productUrl = btn.dataset.productUrl;
        if (productUrl) {
          this.mainElement.innerHTML = '';
          const header = this.querySelector('.product-preview-drawer__head');
          if (header) {
            const titleEl = header.querySelector('.product-preview-drawer__title');
            if (titleEl) titleEl.remove();
          }
          this.open(productUrl);
        }
      });
    });
  }

  setupVariantListeners() {
    const fitInputs = this.mainElement.querySelectorAll('input[name="fit-option"]');
    fitInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.selectedOptions.fit = e.target.value;
        fitInputs.forEach(inp => {
          const label = inp.closest('label');
          if (label) {
            label.classList.toggle('product-preview-drawer__fit-option--selected', inp === e.target);
          }
        });
        this.updateSelectedVariant();
      });
    });
    
    const colorInputs = this.mainElement.querySelectorAll('input[name="color-option"]');
    colorInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.selectedOptions.color = e.target.value;
        colorInputs.forEach(inp => {
          const label = inp.closest('label');
          if (label) {
            label.classList.toggle('product-preview-drawer__color-option--selected', inp === e.target);
          }
        });
        this.updateSelectedVariant();
      });
    });
    
    const sizeInputs = this.mainElement.querySelectorAll('input[name="size-option"]');
    sizeInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.selectedOptions.size = e.target.value;
        this.updateSelectedVariant();
        sizeInputs.forEach(inp => {
          const label = inp.closest('label');
          if (label) {
            label.classList.toggle('product-preview-drawer__size-option--selected', inp === e.target);
          }
        });
      });
    });
  }
  
  async addToCart(form) {
    const formData = new FormData(form);
    const addButton = form.querySelector('button[type="submit"]');
    const originalText = addButton.textContent;
    
    addButton.disabled = true;
    addButton.textContent = 'Aggiungendo...';
    
    const cartAddUrl = (window.routes?.cart_add_url || '/cart/add').replace(/\/?$/, '') + '.js';
    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        body: formData
      });
      
      const contentType = response.headers.get('Content-Type') || '';
      const isJson = contentType.includes('application/json');
      const data = isJson ? await response.json() : {};
      
      if (response.ok) {
        if (data.status && data.status !== 200) {
          throw new Error(data.description || data.message || 'Failed to add to cart');
        }
        await this.refreshCartDrawer();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        this.close();
      } else {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      addButton.textContent = originalText;
      addButton.disabled = false;
      alert(error.message || 'Errore nell\'aggiunta al carrello');
    }
  }

  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') {
      if (typeof updateCartCounters === 'function') updateCartCounters();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
      return;
    }
    const sections = cartDrawer.getSectionsToRender();
    if (!sections || sections.length === 0) {
      if (typeof updateCartCounters === 'function') updateCartCounters();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
      return;
    }
    const sectionIds = sections.map(s => s.section).join(',');
    const path = window.location.pathname || '/';
    const url = `${path}?sections=${encodeURIComponent(sectionIds)}`;
    try {
      const response = await fetch(url);
      const contentType = response.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        if (typeof updateCartCounters === 'function') updateCartCounters();
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
        return;
      }
      const data = await response.json();
      sections.forEach(section => {
        let html = data[section.section];
        if (!html && section.section) {
          const key = Object.keys(data).find(k => k === section.section || String(k).endsWith('__' + section.section));
          if (key) html = data[key];
        }
        if (!html) return;
        const sectionElement = document.querySelector(section.id);
        if (!sectionElement) return;
        try {
          const inner = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (inner) sectionElement.innerHTML = inner.innerHTML;
        } catch (_) {}
      });
      if (typeof updateCartCounters === 'function') updateCartCounters();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
      cartDrawer.open();
    } catch (e) {
      console.error('Error refreshing cart:', e);
      if (typeof updateCartCounters === 'function') updateCartCounters();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
    }
  }
  
  close() {
    this.isOpen = false;
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this.currentProduct = null;
    this.selectedOptions = {};
    this.mainElement.innerHTML = '';
    this.mainElement.style.display = 'none';
    this.loadingElement.style.display = 'block';
    
    const header = this.querySelector('.product-preview-drawer__head');
    if (header) {
      const titleElement = header.querySelector('.product-preview-drawer__title');
      if (titleElement) {
        titleElement.remove();
      }
    }
  }
}

customElements.define('product-preview-drawer', ProductPreviewDrawer);

function initProductPreviewDrawerTriggers() {
  const observer = new MutationObserver(() => {
    document.querySelectorAll('.product-preview-drawer__trigger').forEach(trigger => {
      if (!trigger._previewInit) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const productUrl = trigger.getAttribute('data-product-url');
          const drawer = document.querySelector('product-preview-drawer');
          if (drawer && productUrl) {
            drawer.open(productUrl);
          }
        });
        trigger._previewInit = true;
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.querySelectorAll('.product-preview-drawer__trigger').forEach(trigger => {
    if (!trigger._previewInit) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productUrl = trigger.getAttribute('data-product-url');
        const drawer = document.querySelector('product-preview-drawer');
        if (drawer && productUrl) {
          drawer.open(productUrl);
        }
      });
      trigger._previewInit = true;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductPreviewDrawerTriggers);
} else {
  initProductPreviewDrawerTriggers();
}
